import { useCallback, useEffect, useRef } from "react";
import { TUseTimeoutOptions } from "./useTimeout.types";

const useTimeout = (
    callback: () => void,
    delay: number,
    { autoRun = false }: TUseTimeoutOptions = {}
) => {
    const isFirstRender = useRef(true);
    const callbackRef = useRef(callback);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        callbackRef.current = callback;

        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (autoRun) {
                callbackRef.current();
            }
        }
    }, [callback]);

    const set = useCallback(() => {
        timeoutRef.current = setTimeout(() => callbackRef.current(), delay);
    }, [delay]);

    const clear = useCallback(() => {
        timeoutRef.current && clearTimeout(timeoutRef.current);
    }, []);

    useEffect(() => {
        set();
        return clear;
    }, [delay, set, clear]);

    const reset = useCallback(() => {
        clear();
        set();
    }, [clear, set]);

    return { reset, clear };
};

export default useTimeout;
